import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { ArrowRight, Sparkles } from "lucide-react";

const ACCENT = {
  gold:    { border: "border-[#FBBF24]/40", glow: "shadow-[0_0_40px_-12px_rgba(251,191,36,0.55)]", text: "text-[#FBBF24]", chip: "bg-[#FBBF24]/15 text-[#FBBF24]", btn: "bg-[#FBBF24] text-[#0F2042] hover:bg-yellow-300" },
  crimson: { border: "border-[#DC2626]/40", glow: "shadow-[0_0_40px_-12px_rgba(220,38,38,0.55)]", text: "text-[#FCA5A5]", chip: "bg-[#DC2626]/15 text-[#FCA5A5]", btn: "bg-[#DC2626] text-white hover:bg-red-700" },
  blue:    { border: "border-[#60A5FA]/40", glow: "shadow-[0_0_40px_-12px_rgba(96,165,250,0.55)]", text: "text-[#93C5FD]", chip: "bg-[#60A5FA]/15 text-[#93C5FD]", btn: "bg-[#1E3A8A] text-white hover:bg-[#1E40AF]" },
  emerald: { border: "border-emerald-500/40", glow: "shadow-[0_0_40px_-12px_rgba(16,185,129,0.55)]", text: "text-emerald-200", chip: "bg-emerald-500/15 text-emerald-300", btn: "bg-emerald-600 text-white hover:bg-emerald-500" },
};

/**
 * <AdSlot slot="dashboard-top" /> — fetches active ads for the slot and renders
 * one auto-rotating card every 7s. Click-through navigates to the ad's cta_route.
 *
 * Variants:
 *   - banner (default): horizontal hero card. Use for dashboard-top, shop-banner.
 *   - inline: compact horizontal card. Use for ledger-inline.
 *   - sidebar: vertical narrow card. Use for vault-sidebar.
 *
 * The component renders nothing when there are no active ads for the slot, so
 * pages can safely include it without empty whitespace.
 */
export default function AdSlot({ slot, variant = "banner", className = "" }) {
  const [ads, setAds] = useState([]);
  const [idx, setIdx] = useState(0);
  const nav = useNavigate();

  useEffect(() => {
    let cancelled = false;
    api.get(`/ads?slot=${encodeURIComponent(slot)}`)
      .then((r) => { if (!cancelled) setAds(r.data || []); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [slot]);

  useEffect(() => {
    if (ads.length <= 1) return;
    const t = setInterval(() => setIdx((i) => (i + 1) % ads.length), 7000);
    return () => clearInterval(t);
  }, [ads.length]);

  const ad = ads[idx];
  const palette = useMemo(() => ACCENT[ad?.accent] || ACCENT.gold, [ad?.accent]);

  if (!ad) return null;

  if (variant === "sidebar") {
    return (
      <div
        className={`glass-card border ${palette.border} ${palette.glow} p-4 space-y-2 ${className}`}
        data-testid={`ad-slot-${slot}`}
      >
        <div className={`text-[10px] uppercase tracking-[0.22em] font-bold flex items-center gap-1 ${palette.text}`}>
          <Sparkles size={12} /> ZFD Insider
        </div>
        <div className="font-display text-base font-bold leading-tight text-white">{ad.title}</div>
        <p className="text-xs text-white/65 leading-relaxed">{ad.body}</p>
        {ad.cta_label && ad.cta_route && (
          <button
            onClick={() => nav(ad.cta_route)}
            className={`text-[11px] font-bold uppercase tracking-widest ${palette.text} hover:underline inline-flex items-center gap-1`}
            data-testid={`ad-cta-${ad.id}`}
          >
            {ad.cta_label} <ArrowRight size={12} />
          </button>
        )}
        {ads.length > 1 && (
          <div className="flex gap-1 pt-1">
            {ads.map((a, i) => (
              <span key={a.id ?? `ad-dot-${i}`} className={`h-1 flex-1 rounded-full transition-colors ${i === idx ? palette.text.replace("text-", "bg-") : "bg-white/10"}`} />
            ))}
          </div>
        )}
      </div>
    );
  }

  if (variant === "inline") {
    return (
      <div
        className={`glass-card border ${palette.border} p-4 flex items-center gap-4 flex-wrap ${className}`}
        data-testid={`ad-slot-${slot}`}
      >
        <div className={`shrink-0 px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-[0.2em] ${palette.chip} flex items-center gap-1`}>
          <Sparkles size={11} /> Sponsored Insight
        </div>
        <div className="flex-1 min-w-[220px]">
          <div className="font-bold text-sm text-white">{ad.title}</div>
          <div className="text-xs text-white/60 mt-0.5 leading-relaxed">{ad.body}</div>
        </div>
        {ad.cta_label && ad.cta_route && (
          <button
            onClick={() => nav(ad.cta_route)}
            className={`text-[11px] font-bold uppercase tracking-widest px-3 py-1.5 rounded-md ${palette.btn} flex items-center gap-1 shrink-0`}
            data-testid={`ad-cta-${ad.id}`}
          >
            {ad.cta_label} <ArrowRight size={12} />
          </button>
        )}
      </div>
    );
  }

  // banner (default)
  return (
    <div
      className={`relative overflow-hidden rounded-xl border ${palette.border} ${palette.glow} bg-gradient-to-br from-[#0F2042] via-[#0F2042]/95 to-[#1E3A8A]/40 p-5 md:p-6 ${className}`}
      data-testid={`ad-slot-${slot}`}
    >
      <div className="absolute -top-12 -right-12 w-40 h-40 rounded-full bg-white/[0.04] blur-3xl pointer-events-none" />
      <div className="relative flex flex-col md:flex-row md:items-center gap-4 md:gap-6">
        <div className={`shrink-0 px-2.5 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-[0.2em] ${palette.chip} self-start flex items-center gap-1`}>
          <Sparkles size={11} /> ZFD Spotlight
        </div>
        <div className="flex-1">
          <div className="font-display text-lg md:text-xl font-black leading-tight text-white">{ad.title}</div>
          <p className="text-sm text-white/70 mt-1.5 leading-relaxed max-w-2xl">{ad.body}</p>
        </div>
        {ad.cta_label && ad.cta_route && (
          <button
            onClick={() => nav(ad.cta_route)}
            className={`px-4 py-2.5 rounded-md font-bold text-xs uppercase tracking-widest ${palette.btn} flex items-center gap-2 shrink-0 self-start md:self-center`}
            data-testid={`ad-cta-${ad.id}`}
          >
            {ad.cta_label} <ArrowRight size={14} />
          </button>
        )}
      </div>
      {ads.length > 1 && (
        <div className="relative mt-4 flex gap-1.5">
          {ads.map((a, i) => (
            <button
              key={a.id ?? `ad-nav-${i}`}
              onClick={() => setIdx(i)}
              className={`h-1 rounded-full transition-all ${i === idx ? `w-8 ${palette.text.replace("text-", "bg-")}` : "w-4 bg-white/15 hover:bg-white/25"}`}
              data-testid={`ad-dot-${i}`}
              aria-label={`Show ad ${i + 1}`}
            />
          ))}
        </div>
      )}
    </div>
  );
}
